// Slice 41 — Licenses screen. Header (title + counts + Issue-key primary),
// filter bar (search + tier-segmented + status-segmented), table inside a
// padding-less HqCard, per-row revoke/unrevoke icon-button. Cursor "Load more".
//
// State coordination with the shell:
//   - registerIssueHandler(open)  → ⌘N + sidebar CTA open this screen's modal
//   - bumpRefresh()               → after issue/revoke, the table re-fetches
//   - useEffect on refreshTick    → re-fetch when any other screen bumps too

import { useCallback, useEffect, useMemo, useState } from "react";
import type { LicenseRow, TelemetrySummary, Tier } from "../../api/client";
import { Api, TelemetryApi, listLicensesWithPage } from "../../api/client";
import { useRouter } from "../../router/router";
import { useShell } from "../../shell/HqShellContext";
import { HqAvatar, HqBadge, HqButton, HqCard, HqIconButton, HqInput, Icons } from "../../ui";
import { IssueLicenseModal } from "./IssueLicenseModal";
import { MonoId } from "./MonoId";
import { RevokeModal } from "./RevokeModal";
import { expiryTone, fmtDateMs, fmtDateSec } from "./format";

type TierFilter = "all" | Tier;
type StatusFilter = "all" | "active" | "revoked";
type ExpiryFilter = "all" | "expiring" | "expired";

const TIER_FILTERS: { value: TierFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "beta", label: "Beta" },
  { value: "pro", label: "Pro" },
  { value: "free", label: "Free" },
];

const STATUS_FILTERS: { value: StatusFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "active", label: "Active" },
  { value: "revoked", label: "Revoked" },
];

const EXPIRY_FILTERS: { value: ExpiryFilter; label: string }[] = [
  { value: "all", label: "Any" },
  { value: "expiring", label: "≤14 d" },
  { value: "expired", label: "Expired" },
];

export function LicensesScreen() {
  const { registerIssueHandler, bumpRefresh, refreshTick, toast } = useShell();
  const { navigate } = useRouter();
  const [rows, setRows] = useState<LicenseRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [tier, setTier] = useState<TierFilter>("all");
  const [status, setStatus] = useState<StatusFilter>("all");

  const [expiry, setExpiry] = useState<ExpiryFilter>("all");
  const [summary, setSummary] = useState<TelemetrySummary | null>(null);

  const [issueOpen, setIssueOpen] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<LicenseRow | null>(null);

  // Slice 55 — pull the telemetry-summary KPIs (expiringLicensesCount +
  // expiredLicensesCount + activeInstances) so the strip renders against the
  // *full* customer base, not just the page we loaded.
  useEffect(() => {
    void refreshTick;
    let cancelled = false;
    void (async () => {
      const res = await TelemetryApi.summary(30);
      if (cancelled) return;
      if (res.ok) setSummary(res.data);
    })();
    return () => {
      cancelled = true;
    };
  }, [refreshTick]);

  // Initial + filter-change fetch. The fetch logic lives inside the effect
  // (not a useCallback) so biome's exhaustive-deps stays happy — we don't
  // want a stale fetchPage closure outliving its filter snapshot.
  // `refreshTick` is read so bumpRefresh() from elsewhere (issue/revoke)
  // re-fires the load; the `void` marks it load-bearing for biome.
  useEffect(() => {
    void refreshTick;
    let cancelled = false;
    setLoading(true);
    setErr(null);
    void (async () => {
      const params = {
        cursor: null,
        limit: 50,
        q: q.trim() || undefined,
        revoked: status === "active" ? false : status === "revoked" ? true : undefined,
        ...(tier !== "all" ? { tier } : {}),
        ...(expiry !== "all" ? { expiry } : {}),
      };
      const res = await listLicensesWithPage(params);
      if (cancelled) return;
      setLoading(false);
      if (!res.ok) {
        setErr(res.error.message);
        return;
      }
      setRows(res.data.data);
      setNextCursor(res.data.page.cursor);
    })();
    return () => {
      cancelled = true;
    };
  }, [q, status, tier, expiry, refreshTick]);

  const loadMore = useCallback(async () => {
    if (!nextCursor) return;
    setLoading(true);
    const res = await listLicensesWithPage({
      cursor: nextCursor,
      limit: 50,
      q: q.trim() || undefined,
      revoked: status === "active" ? false : status === "revoked" ? true : undefined,
      ...(tier !== "all" ? { tier } : {}),
      ...(expiry !== "all" ? { expiry } : {}),
    });
    setLoading(false);
    if (!res.ok) {
      toast(res.error.message, "danger");
      return;
    }
    setRows((prev) => [...prev, ...res.data.data]);
    setNextCursor(res.data.page.cursor);
  }, [nextCursor, q, status, tier, expiry, toast]);

  // Wire ⌘N / sidebar CTA to the local Issue modal while this screen is mounted.
  useEffect(() => {
    registerIssueHandler(() => setIssueOpen(true));
    return () => registerIssueHandler(null);
  }, [registerIssueHandler]);

  const counts = useMemo(() => {
    const active = rows.filter((r) => !r.revoked).length;
    return { total: rows.length, active };
  }, [rows]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <Header total={counts.total} active={counts.active} onIssue={() => setIssueOpen(true)} />
      <KpiStrip summary={summary} />
      <FilterBar
        q={q}
        onQ={setQ}
        tier={tier}
        onTier={setTier}
        status={status}
        onStatus={setStatus}
        expiry={expiry}
        onExpiry={setExpiry}
      />
      <HqCard padding={false}>
        <Table
          rows={rows}
          loading={loading}
          err={err}
          onOpen={(r) => navigate("licenses", r.licenseId)}
          onRevoke={(r) => setRevokeTarget(r)}
          onUnrevoke={async (r) => {
            const res = await Api.unrevokeLicense(r.licenseId);
            if (!res.ok) {
              toast(res.error.message, "danger");
              return;
            }
            toast("Un-revoked.", "success");
            bumpRefresh();
          }}
        />
        {nextCursor && (
          <div style={{ padding: 12, display: "flex", justifyContent: "center" }}>
            <HqButton variant="secondary" size="sm" onClick={() => void loadMore()}>
              Load more
            </HqButton>
          </div>
        )}
      </HqCard>

      <IssueLicenseModal
        open={issueOpen}
        onClose={() => setIssueOpen(false)}
        onIssued={() => {
          setIssueOpen(false);
          bumpRefresh();
          toast("License signed and saved.", "success");
        }}
      />
      <RevokeModal
        target={revokeTarget}
        onClose={() => setRevokeTarget(null)}
        onRevoked={() => {
          setRevokeTarget(null);
          bumpRefresh();
          toast("License revoked.", "success");
        }}
      />
    </div>
  );
}

function Header({
  total,
  active,
  onIssue,
}: {
  total: number;
  active: number;
  onIssue: () => void;
}) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
      <Icons.Key width={20} height={20} sw={1.7} style={{ color: "var(--hq-accent)" }} />
      <div style={{ display: "flex", flexDirection: "column" }}>
        <h1
          style={{
            margin: 0,
            fontSize: 24,
            fontWeight: 600,
            letterSpacing: "-.02em",
            color: "var(--hq-text)",
            lineHeight: 1.2,
          }}
        >
          Licenses
        </h1>
        <span className="hq-mono" style={{ fontSize: 11, color: "var(--hq-text-dim)" }}>
          {total} total · {active} active
        </span>
      </div>
      <span style={{ flex: 1 }} />
      <HqButton variant="primary" leadingIcon={Icons.Plus} onClick={onIssue}>
        Issue key
      </HqButton>
    </div>
  );
}

function FilterBar({
  q,
  onQ,
  tier,
  onTier,
  status,
  onStatus,
  expiry,
  onExpiry,
}: {
  q: string;
  onQ: (v: string) => void;
  tier: TierFilter;
  onTier: (v: TierFilter) => void;
  status: StatusFilter;
  onStatus: (v: StatusFilter) => void;
  expiry: ExpiryFilter;
  onExpiry: (v: ExpiryFilter) => void;
}) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
      <div style={{ flex: 1, maxWidth: 360 }}>
        <HqInput
          placeholder="Search license ID or customer…"
          leadingIcon={Icons.Search}
          value={q}
          onChange={(e) => onQ(e.target.value)}
        />
      </div>
      <Segmented options={TIER_FILTERS} value={tier} onChange={(v) => onTier(v as TierFilter)} />
      <Segmented
        options={STATUS_FILTERS}
        value={status}
        onChange={(v) => onStatus(v as StatusFilter)}
      />
      <Segmented
        options={EXPIRY_FILTERS}
        value={expiry}
        onChange={(v) => onExpiry(v as ExpiryFilter)}
      />
    </div>
  );
}

function KpiStrip({ summary }: { summary: TelemetrySummary | null }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 10 }}>
      <Kpi label="Active instances" value={summary ? summary.activeInstances.toString() : "…"} />
      <Kpi
        label="Expiring ≤14d"
        value={summary ? summary.expiringLicensesCount.toString() : "…"}
        tone={summary && summary.expiringLicensesCount > 0 ? "warn" : "neutral"}
      />
      <Kpi
        label="Expired (active)"
        value={summary ? summary.expiredLicensesCount.toString() : "…"}
        tone={summary && summary.expiredLicensesCount > 0 ? "danger" : "neutral"}
      />
      <Kpi
        label="Error rate (30d)"
        value={summary ? `${(summary.errorRate * 100).toFixed(1)}%` : "…"}
        tone={summary && summary.errorRate > 0.05 ? "warn" : "neutral"}
      />
    </div>
  );
}

function Kpi({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: "warn" | "danger" | "neutral";
}) {
  const color =
    tone === "warn" ? "var(--hq-warn)" : tone === "danger" ? "var(--hq-danger)" : "var(--hq-text)";
  return (
    <div
      style={{
        padding: 12,
        background: "var(--hq-bg-input)",
        border: "1px solid var(--hq-border-input)",
        borderRadius: "var(--hq-radius-md)",
        display: "flex",
        flexDirection: "column",
        gap: 4,
      }}
    >
      <span
        className="hq-mono"
        style={{
          fontSize: 9.5,
          color: "var(--hq-text-dim)",
          letterSpacing: ".1em",
          textTransform: "uppercase",
        }}
      >
        {label}
      </span>
      <span style={{ fontSize: 20, fontWeight: 600, color }}>{value}</span>
    </div>
  );
}

function Segmented({
  options,
  value,
  onChange,
}: {
  options: { value: string; label: string }[];
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div
      style={{
        display: "inline-flex",
        padding: 3,
        gap: 2,
        background: "var(--hq-bg-input)",
        borderRadius: "var(--hq-radius-lg)",
        border: "1px solid var(--hq-border-input)",
      }}
    >
      {options.map((o) => {
        const active = o.value === value;
        return (
          <button
            type="button"
            key={o.value}
            onClick={() => onChange(o.value)}
            style={{
              padding: "0 10px",
              height: 26,
              borderRadius: "var(--hq-radius-md)",
              background: active ? "var(--hq-bg-active)" : "transparent",
              color: active ? "var(--hq-text)" : "var(--hq-text-muted)",
              fontSize: 11.5,
              border: "none",
              cursor: "pointer",
              fontWeight: 500,
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function Table({
  rows,
  loading,
  err,
  onOpen,
  onRevoke,
  onUnrevoke,
}: {
  rows: LicenseRow[];
  loading: boolean;
  err: string | null;
  onOpen: (r: LicenseRow) => void;
  onRevoke: (r: LicenseRow) => void;
  onUnrevoke: (r: LicenseRow) => void;
}) {
  if (err) return <Empty message={err} tone="danger" />;
  if (rows.length === 0 && loading) return <Empty message="Loading licenses…" tone="dim" />;
  if (rows.length === 0)
    return <Empty message="No licenses yet. Click “Issue key” to mint the first one." tone="dim" />;
  return (
    <div style={{ maxHeight: 600, overflow: "auto" }} className="hq-scroll">
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
        <thead>
          <Th>
            <Cell head>License ID</Cell>
            <Cell head>Customer</Cell>
            <Cell head>Tier</Cell>
            <Cell head>Issued</Cell>
            <Cell head>Expires</Cell>
            <Cell head>HWID</Cell>
            <Cell head>Status</Cell>
            <Cell head right />
          </Th>
        </thead>
        <tbody>
          {rows.map((r) => (
            <Row
              key={r.licenseId}
              row={r}
              onOpen={onOpen}
              onRevoke={onRevoke}
              onUnrevoke={onUnrevoke}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <tr
      style={{
        position: "sticky",
        top: 0,
        background: "var(--hq-bg-raised)",
        borderBottom: "1px solid var(--hq-border)",
        textAlign: "left",
        zIndex: 1,
      }}
    >
      {children}
    </tr>
  );
}

function Cell({
  children,
  head,
  right,
}: {
  children?: React.ReactNode;
  head?: boolean;
  right?: boolean;
}) {
  return (
    <td
      style={{
        padding: head ? "10px 14px" : "12px 14px",
        textAlign: right ? "right" : "left",
        color: head ? "var(--hq-text-dim)" : "var(--hq-text)",
        fontFamily: head ? "var(--hq-font-mono)" : "var(--hq-font-sans)",
        fontSize: head ? 10.5 : 12,
        letterSpacing: head ? ".1em" : 0,
        textTransform: head ? "uppercase" : "none",
        fontWeight: head ? 500 : 400,
        verticalAlign: "middle",
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </td>
  );
}

function Row({
  row,
  onOpen,
  onRevoke,
  onUnrevoke,
}: {
  row: LicenseRow;
  onOpen: (r: LicenseRow) => void;
  onRevoke: (r: LicenseRow) => void;
  onUnrevoke: (r: LicenseRow) => void;
}) {
  const tone = row.tier === "pro" ? "pro" : row.tier === "beta" ? "beta" : "free";
  const expTone = expiryTone(row.expiresAt);
  // Click handler on the row navigates to detail; action-cells stopPropagation
  // so revoke/unrevoke icon-buttons don't double-fire.
  return (
    <tr
      style={{
        borderBottom: "1px solid var(--hq-border)",
        cursor: "pointer",
      }}
      onClick={() => onOpen(row)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen(row);
        }
      }}
      tabIndex={0}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLTableRowElement).style.background = "var(--hq-bg-hover)";
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLTableRowElement).style.background = "transparent";
      }}
    >
      <Cell>
        <MonoId value={row.licenseId} short={14} />
      </Cell>
      <Cell>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
          <HqAvatar name={row.customerId} size={22} />
          <span style={{ fontFamily: "var(--hq-font-mono)", fontSize: 11 }}>{row.customerId}</span>
        </span>
      </Cell>
      <Cell>
        <HqBadge tone={tone}>{row.tier}</HqBadge>
      </Cell>
      <Cell>
        <span
          style={{ fontFamily: "var(--hq-font-mono)", color: "var(--hq-text-muted)", fontSize: 11 }}
        >
          {fmtDateMs(row.createdAt)}
        </span>
      </Cell>
      <Cell>
        <span
          style={{
            fontFamily: "var(--hq-font-mono)",
            fontSize: 11,
            color:
              expTone === "danger"
                ? "var(--hq-danger)"
                : expTone === "warn"
                  ? "var(--hq-warn)"
                  : row.expiresAt
                    ? "var(--hq-text-muted)"
                    : "var(--hq-text-faint)",
          }}
        >
          {fmtDateSec(row.expiresAt)}
        </span>
      </Cell>
      <Cell>
        <HqBadge tone={row.hwidFingerprint ? "neutral" : "warn"} size="sm">
          {row.hwidFingerprint ? "bound" : "unbound"}
        </HqBadge>
      </Cell>
      <Cell>
        {row.revoked ? (
          <HqBadge tone="danger">revoked</HqBadge>
        ) : (
          <HqBadge tone="success">active</HqBadge>
        )}
      </Cell>
      <Cell right>
        <span onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
          {row.revoked ? (
            <HqIconButton
              icon={Icons.Check}
              size="sm"
              title="Un-revoke"
              onClick={() => onUnrevoke(row)}
            />
          ) : (
            <HqIconButton
              icon={Icons.Revoke}
              size="sm"
              title="Revoke"
              onClick={() => onRevoke(row)}
            />
          )}
        </span>
      </Cell>
    </tr>
  );
}

function Empty({ message, tone }: { message: string; tone: "dim" | "danger" }) {
  return (
    <div
      style={{
        padding: 48,
        textAlign: "center",
        color: tone === "danger" ? "var(--hq-danger)" : "var(--hq-text-dim)",
        fontSize: 13,
      }}
    >
      {message}
    </div>
  );
}
